import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

export type StudioPageChromeTab = {
  key: string;
  label: string;
  shortLabel?: string;
};

export type StudioPageChromeAction = {
  key: string;
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary";
  disabled?: boolean;
};

export type StudioPageCrumb = { to: string; label: string };

export type StudioPageChrome = {
  title?: string;
  /** Pages between the section home and this page, e.g. Karkhana › Costing › Saree 18214. */
  crumbs?: StudioPageCrumb[];
  subtitle?: string;
  onBack?: () => void;
  tabs?: StudioPageChromeTab[];
  activeTab?: string;
  onTabChange?: (key: string) => void;
  actions?: StudioPageChromeAction[];
};

type StudioPageChromeContextValue = {
  chrome: StudioPageChrome;
  setChrome: (chrome: StudioPageChrome) => void;
};

const StudioPageChromeContext = createContext<StudioPageChromeContextValue>({
  chrome: {},
  setChrome: () => {},
});

export const StudioPageChromeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [chrome, setChromeState] = useState<StudioPageChrome>({});
  const setChrome = useCallback((next: StudioPageChrome) => {
    setChromeState(next);
  }, []);
  return (
    <StudioPageChromeContext.Provider value={{ chrome, setChrome }}>
      {children}
    </StudioPageChromeContext.Provider>
  );
};

export const useStudioPageChrome = () => useContext(StudioPageChromeContext);

export const useStudioPageChromeOverride = (chrome: StudioPageChrome) => {
  const { setChrome } = useStudioPageChrome();
  const onBackRef = useRef(chrome.onBack);
  const onTabChangeRef = useRef(chrome.onTabChange);
  const actionsRef = useRef(chrome.actions);
  useEffect(() => {
    onBackRef.current = chrome.onBack;
    onTabChangeRef.current = chrome.onTabChange;
    actionsRef.current = chrome.actions;
  });
  const tabSignature = (chrome.tabs || [])
    .map((tab) => `${tab.key}:${tab.label}:${tab.shortLabel || ""}`)
    .join("|");
  const actionSignature = (chrome.actions || [])
    .map((action) => `${action.key}:${action.label}:${action.variant || ""}:${action.disabled ? "1" : "0"}`)
    .join("|");

  const crumbSignature = (chrome.crumbs || []).map((c) => `${c.to}:${c.label}`).join("|");

  useEffect(() => {
    const onBack = chrome.onBack ? () => onBackRef.current?.() : undefined;
    const onTabChange = chrome.onTabChange
      ? (key: string) => onTabChangeRef.current?.(key)
      : undefined;
    const actions = (chrome.actions || []).map((action) => ({
      ...action,
      onClick: () => {
        actionsRef.current?.find((item) => item.key === action.key)?.onClick();
      },
    }));
    setChrome({
      title: chrome.title,
      crumbs: chrome.crumbs,
      subtitle: chrome.subtitle,
      onBack,
      tabs: chrome.tabs,
      activeTab: chrome.activeTab,
      onTabChange,
      actions: actions.length ? actions : undefined,
    });
    return () => setChrome({});
  }, [
    chrome.title,
    chrome.subtitle,
    crumbSignature,
    Boolean(chrome.onBack),
    Boolean(chrome.onTabChange),
    chrome.activeTab,
    tabSignature,
    actionSignature,
    setChrome,
  ]);
};
